import React, { useEffect, useState } from 'react';
import BarChart from './BarChart';
import { fetchJson } from './adminApi';
import { kr, typeLabel } from './adminFormat';

const sortedEntries = (obj) => Object.entries(obj || {}).sort((a, b) => b[1] - a[1]);
const EXTRAS_HINT = 'No data yet. BRF, energy class and construction year come from the extras step of a scrape job.';
const DAILY_DAYS = 30;

const ChartCard = ({ title, children }) => (
    <section className="ax-card">
        <h3>{title}</h3>
        {children}
    </section>
);

// Active listings per day, recorded after every scrape (listingActiveDailyTotal).
const useDailyTotals = () => {
    const [series, setSeries] = useState(null);
    useEffect(() => {
        let active = true;
        fetchJson(`/admin/stats/listings?days=${DAILY_DAYS}`)
            .then((payload) => {
                const items = Array.isArray(payload) ? payload : (payload?.days || payload?.series || payload?.data || []);
                const byDate = new Map(items.map((item) => (Array.isArray(item)
                    ? [String(item[0]), Number(item[1]) || 0]
                    : [String(item?.date || item?.day || ''), Number(item?.count ?? item?.total ?? item?.listings) || 0])));
                const today = new Date();
                const keys = Array.from({ length: DAILY_DAYS }, (_, index) => {
                    const day = new Date(today);
                    day.setDate(today.getDate() - (DAILY_DAYS - 1 - index));
                    return day.toLocaleDateString('sv-SE');
                });
                if (active) setSeries({ labels: keys.map((k) => k.slice(5)), values: keys.map((k) => byDate.get(k) || 0) });
            })
            .catch(() => active && setSeries({ labels: [], values: [] }));
        return () => {
            active = false;
        };
    }, []);
    return series;
};

const ChartsTab = ({ charts }) => {
    const daily = useDailyTotals();
    if (!charts) return <p className="ax-muted">Loading…</p>;
    if (!charts.price_hist) return <p className="ax-muted">No listings yet.</p>;
    const sqm = sortedEntries(charts.sqm_by_type);
    const tenure = sortedEntries(charts.tenure);
    const agencies = sortedEntries(charts.agencies);
    return (
        <>
            <div className="ax-grid ax-grid--two">
                <ChartCard title="Asking price">
                    <BarChart labels={charts.price_hist.labels} values={charts.price_hist.values} />
                </ChartCard>
                <ChartCard title="Living area (m²)">
                    <BarChart labels={charts.area_hist.labels} values={charts.area_hist.values} />
                </ChartCard>
            </div>
            <div className="ax-grid ax-grid--two">
                <ChartCard title="Listings per kommun (top 25)">
                    <BarChart horizontal size="tall" labels={charts.top_kommuner.labels} values={charts.top_kommuner.count} />
                </ChartCard>
                <ChartCard title="Median price per m² by kommun (same 25)">
                    <BarChart horizontal size="tall" labels={charts.top_kommuner.labels} values={charts.top_kommuner.median_sqm} format={kr} />
                </ChartCard>
            </div>
            <div className="ax-grid ax-grid--three">
                <ChartCard title="Rooms">
                    <BarChart labels={charts.rooms.labels} values={charts.rooms.values} />
                </ChartCard>
                <ChartCard title="Median price per m² by type">
                    <BarChart labels={sqm.map(([k]) => typeLabel(k))} values={sqm.map(([, v]) => v)} format={kr} />
                </ChartCard>
                <ChartCard title="Tenure form">
                    <BarChart horizontal labels={tenure.map(([k]) => k)} values={tenure.map(([, v]) => v)} emptyHint={EXTRAS_HINT} />
                </ChartCard>
            </div>
            <div className="ax-grid ax-grid--two">
                <ChartCard title="Newly published listings per day (last 60 days with listings)">
                    <BarChart labels={charts.published.labels} values={charts.published.values} />
                </ChartCard>
                <ChartCard title={`Active listings per day (last ${DAILY_DAYS} days)`}>
                    {daily ? (
                        <BarChart
                            labels={daily.labels}
                            values={daily.values}
                            emptyHint="No daily totals yet. One is recorded after every scrape job."
                        />
                    ) : <p className="ax-muted">Loading…</p>}
                </ChartCard>
            </div>
            <div className="ax-grid ax-grid--three">
                <ChartCard title="Construction decade">
                    <BarChart labels={charts.decades.labels} values={charts.decades.values} emptyHint={EXTRAS_HINT} />
                </ChartCard>
                <ChartCard title="Energy class">
                    <BarChart labels={Object.keys(charts.energy || {})} values={Object.values(charts.energy || {})} emptyHint={EXTRAS_HINT} />
                </ChartCard>
                <ChartCard title="Top 15 agencies">
                    <BarChart horizontal labels={agencies.map(([k]) => k)} values={agencies.map(([, v]) => v)} emptyHint={EXTRAS_HINT} />
                </ChartCard>
            </div>
        </>
    );
};

export default ChartsTab;
